import type { CliPromptTurnState } from '@buildautomaton/types';
import { sendWsMessage } from '../../connection/create-ws-bridge.js';
import type { MessageHandlerDeps } from '../../routing/message-handler-deps.js';
import { sendPromptQueueClientReport } from '../client-report.js';
import { mergeBridgeServerQueueSnapshot, readPersistedQueue, writePersistedQueue } from '../disk-store.js';
import { dispatchLocalPrompt } from './dispatch-local-prompt.js';
import { hasRunningTurn, pickNextRunnableTurn } from './queue-selection.js';
import {
  deleteRunIdQueueKey,
  getRunIdQueueKey,
  setRunIdQueueKey,
  syncRunningTurnQueueKeys,
} from './run-id-queue-key-map.js';
import { runLocalRevertBeforeQueuedPrompt } from './run-local-revert-before-queued-prompt.js';
import { finalizePromptTurnOnBridge } from './finalize-prompt-turn-on-bridge.js';

/**
 * Apply a `prompt_queue_state` snapshot from the bridge, persist per-queue files, sync CLI state, then start at most one local run per queue.
 */
export async function applyPromptQueueStateFromServer(
  msg: { queues?: Record<string, unknown[]> },
  deps: MessageHandlerDeps,
): Promise<void> {
  const raw = msg.queues;
  if (!raw || typeof raw !== 'object') return;
  const getWs = deps.getWs;

  for (const [queueKey, serverTurns] of Object.entries(raw)) {
    if (!Array.isArray(serverTurns)) continue;
    const file = await mergeBridgeServerQueueSnapshot(queueKey, serverTurns);
    await writePersistedQueue(file);
  }

  for (const [queueKey, serverTurns] of Object.entries(raw)) {
    if (!Array.isArray(serverTurns)) continue;
    const file = await readPersistedQueue(queueKey);
    if (!file) continue;
    syncRunningTurnQueueKeys(file.turns, queueKey);
  }

  for (const [queueKey, serverTurns] of Object.entries(raw)) {
    if (!Array.isArray(serverTurns)) continue;
    const file = await readPersistedQueue(queueKey);
    if (!file) continue;
    const cancelRow = file.turns.find((t) => t.bridgeServerState === 'cancel_requested' && t.lastCliState === 'running');
    if (cancelRow) {
      const localCancelHandled = await deps.acpManager.cancelRun(cancelRow.turnId);
      if (!localCancelHandled) {
        deps.log(
          `[Queue] bridge server cancel_requested for ${cancelRow.turnId.slice(0, 8)}… but no local agent run is active (e.g. after CLI restart); marking cancelled and notifying bridge.`,
        );
        await finalizePromptTurnOnBridge(deps.getWs, cancelRow.turnId, false, { terminalCliState: 'cancelled' });
        const ws = deps.getWs();
        if (ws && cancelRow.sessionId) {
          sendWsMessage(ws, {
            type: 'prompt_result',
            sessionId: cancelRow.sessionId,
            runId: cancelRow.turnId,
            success: false,
            error: 'Stopped by user',
            stopReason: 'cancelled',
          });
        }
      }
    }
  }

  const report: Record<string, Array<{ turnId: string; cliState: CliPromptTurnState | null }>> = {};
  const startedThisTick = new Set<string>();

  for (const [queueKey, serverTurns] of Object.entries(raw)) {
    if (!Array.isArray(serverTurns)) continue;
    const file = await readPersistedQueue(queueKey);
    if (!file) continue;
    if (hasRunningTurn(file.turns)) continue;
    const next = pickNextRunnableTurn(file.turns);
    if (!next) continue;
    if (!(await runLocalRevertBeforeQueuedPrompt(next, deps))) {
      next.lastCliState = 'failed';
      await writePersistedQueue(file);
      sendPromptQueueClientReport(getWs(), { [queueKey]: [{ turnId: next.turnId, cliState: 'failed' }] });
      continue;
    }
    next.lastCliState = 'running';
    await writePersistedQueue(file);
    setRunIdQueueKey(next.turnId, queueKey);
    startedThisTick.add(next.turnId);
    report[queueKey] = [{ turnId: next.turnId, cliState: 'running' }];
  }

  if (Object.keys(report).length > 0) {
    sendPromptQueueClientReport(getWs(), report);
  }

  for (const [queueKey, serverTurns] of Object.entries(raw)) {
    if (!Array.isArray(serverTurns)) continue;
    const file = await readPersistedQueue(queueKey);
    if (!file) continue;
    const running = file.turns.find((t) => t.lastCliState === 'running');
    if (!running || !startedThisTick.has(running.turnId)) continue;
    if (getRunIdQueueKey(running.turnId) !== queueKey) continue;
    dispatchLocalPrompt(running, deps);
  }
}
